import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, AlertTriangle, AlertOctagon, Wrench, Calendar, RefreshCw, ExternalLink, Loader2, Info, Boxes, ChevronRight, BellRing, Rss, TrendingUp, TrendingDown, Minus, History } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusUptimeGrid } from "@/components/StatusUptimeGrid";
import { inMaintenanceWindow, minutesToWindowEnd, fmtLeft, fmtHour } from "@/components/StatusBar";

// Iter 110d — Native renderer for the incident.io status page.
// The vendor's hosted page ships X-Frame-Options: SAMEORIGIN and CSP
// frame-ancestors 'self', so an iframe embed can never work. This
// component fetches the Widget API JSON through the Conduit backend
// proxy (which caches ~60s) and paints a first-party status page.

const STATUS_META = {
  investigating:      { label: "Investigating",      cls: "bg-red-100 text-red-800 border-red-200",         icon: AlertOctagon },
  identified:         { label: "Identified",         cls: "bg-amber-100 text-amber-800 border-amber-200",   icon: AlertTriangle },
  monitoring:         { label: "Monitoring",         cls: "bg-blue-100 text-blue-800 border-blue-200",      icon: Info },
  resolved:           { label: "Resolved",           cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  maintenance_scheduled:   { label: "Scheduled",     cls: "bg-slate-100 text-slate-700 border-slate-200",   icon: Calendar },
  maintenance_in_progress: { label: "In progress",   cls: "bg-blue-100 text-blue-800 border-blue-200",      icon: Wrench },
  maintenance_complete:    { label: "Complete",      cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
};

const COMPONENT_STATUS_META = {
  operational:         { label: "Operational",         cls: "bg-emerald-100 text-emerald-700" },
  under_maintenance:   { label: "Under maintenance",   cls: "bg-slate-100 text-slate-700" },
  degraded_performance:{ label: "Degraded",            cls: "bg-amber-100 text-amber-800" },
  partial_outage:      { label: "Partial outage",      cls: "bg-orange-100 text-orange-800" },
  full_outage:         { label: "Full outage",         cls: "bg-red-100 text-red-800" },
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

// Iter 121 — uptime % pill, worded like the public page ("100.00% uptime").
// Iter 122 — 30-day figure shown alongside so trends are visible.
function UptimePct({ value, value30, since, days, testid }) {
  if (value == null) {
    return (
      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-400"
            data-testid={testid} title="No uptime data available yet">
        No data
      </span>
    );
  }
  const toneFor = (v) => v >= 99.9 ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
    : v >= 99 ? "bg-amber-50 text-amber-800 ring-amber-200"
    : "bg-rose-50 text-rose-700 ring-rose-200";
  const trend = value30 == null ? null
    : value30 > value + 0.005 ? "up"
    : value30 < value - 0.005 ? "down" : "flat";
  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      {value30 != null && (
        <span className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ring-1 ${toneFor(value30)}`}
              data-testid={`${testid}-30d`}
              title={`${value30.toFixed(2)}% uptime over the last 30 days — computed from published incident impact windows (degraded periods count as downtime, routine maintenance does not)${
                trend === "up" ? " · improving vs the 90-day figure"
                : trend === "down" ? " · worse than the 90-day figure" : ""}`}>
          {trend === "up" && <TrendingUp className="h-2.5 w-2.5" />}
          {trend === "down" && <TrendingDown className="h-2.5 w-2.5" />}
          {trend === "flat" && <Minus className="h-2.5 w-2.5 opacity-60" />}
          30d {value30.toFixed(2)}%
        </span>
      )}
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ring-1 ${toneFor(value)}`}
            data-testid={testid}
            title={`${value.toFixed(2)}% uptime over the last ${days || 90} days${since ? ` · data since ${new Date(since).toLocaleDateString()}` : ""}`}>
        {value.toFixed(2)}% uptime
      </span>
    </span>
  );
}

const fmtDuration = (secs) => {
  if (secs == null) return "ongoing";
  const m = Math.round(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 24 ? (m % 60 ? `${h}h ${m % 60}m` : `${h}h`) : `${Math.floor(h / 24)}d ${h % 24}h`;
};

// Iter 122 — resolved incidents & completed maintenances from /history.
function IncidentHistory({ items, publicUrl }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <section data-testid="status-history-section">
      <div className="mb-2 flex items-center gap-2">
        <History className="h-4 w-4 text-slate-500" />
        <h4 className="font-heading text-sm font-semibold text-slate-800">Incident history</h4>
        <span className="text-[11px] text-slate-400">{items.length} past event{items.length === 1 ? "" : "s"}</span>
        {publicUrl && (
          <a href={`${publicUrl.replace(/\/$/, "")}/history`} target="_blank" rel="noopener noreferrer"
             data-testid="status-history-full-link"
             className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-primary">
            Full history <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <Card className="max-h-[360px] divide-y divide-slate-100 overflow-y-auto p-0"
            data-testid="status-history-scroll">
        {items.map((it) => {
          const maint = it.type === "maintenance";
          const open = expanded === it.id;
          return (
            <div key={it.id} className="px-4 py-3" data-testid={`status-history-item-${it.id}`}>
              <button type="button" className="flex w-full items-start gap-2 text-left"
                      onClick={() => setExpanded(open ? null : it.id)}
                      data-testid={`status-history-toggle-${it.id}`}>
                {maint ? <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
                       : <AlertOctagon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">{it.name}</span>
                  <span className="mt-0.5 block text-[11px] text-slate-400">
                    {it.started_at ? fmtDate(it.started_at) : "—"}
                    {it.ended_at ? ` → ${new Date(it.ended_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : " · still open"}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    maint ? "bg-slate-100 text-slate-600" : "bg-rose-50 text-rose-700"}`}
                    data-testid={`status-history-duration-${it.id}`}>
                    {fmtDuration(it.duration_seconds)}
                  </span>
                  <ChevronRight className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
                </span>
              </button>
              {open && (
                <div className="mt-2 pl-6" data-testid={`status-history-detail-${it.id}`}>
                  {it.last_update && (
                    <p className="whitespace-pre-line text-[12px] leading-relaxed text-slate-600">{it.last_update.replace(/(\*\*|__|[*_])/g, "")}</p>
                  )}
                  {it.components?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {it.components.map((c, i) => (
                        <span key={`${c}-${i}`} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{c}</span>
                      ))}
                    </div>
                  )}
                  {it.url && (
                    <a href={it.url} target="_blank" rel="noopener noreferrer"
                       className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline">
                      Open on status page <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Card>
    </section>
  );
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "Unknown", cls: "bg-slate-100 text-slate-700 border-slate-200", icon: Info };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function ComponentChips({ components }) {
  if (!components || components.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {components.map((c, i) => {
        const meta = COMPONENT_STATUS_META[c.status] || { label: c.status || "unknown", cls: "bg-slate-100 text-slate-700" };
        return (
          <span key={c.id || i} className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${meta.cls}`}
                data-testid={`status-component-${c.id || i}`}>
            {c.name || c.id}
            <span className="ml-1 opacity-70">· {meta.label}</span>
          </span>
        );
      })}
    </div>
  );
}

function IncidentCard({ item, testid }) {
  // incident.io payload shape:
  //   id, name, status, url (public link), started_at, updated_at
  //   affected_components: [{ id, name, status }]
  //   updates: [{ message, status, published_at }]
  const latest = (item.updates && item.updates[0]) || null;
  return (
    <Card className="border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-heading text-sm font-semibold text-slate-800 truncate">{item.name || "Untitled"}</h4>
            <StatusPill status={item.status} />
          </div>
          <div className="mt-1 text-[11px] text-slate-400">
            {item.started_at && <>Started {fmtDate(item.started_at)}</>}
            {item.updated_at && item.updated_at !== item.started_at && <> · Updated {fmtDate(item.updated_at)}</>}
            {item.scheduled_start_at && <>Starts {fmtDate(item.scheduled_start_at)}</>}
            {item.scheduled_end_at && <> · Ends {fmtDate(item.scheduled_end_at)}</>}
          </div>
        </div>
        {item.url && (
          <a href={item.url} target="_blank" rel="noopener noreferrer"
             className="shrink-0 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 hover:border-primary/40 hover:text-primary">
            <ExternalLink className="h-3 w-3 inline -mt-0.5" /> Details
          </a>
        )}
      </div>
      {latest?.message && (
        <p className="mt-3 text-sm text-slate-600 whitespace-pre-line">{latest.message}</p>
      )}
      <ComponentChips components={item.affected_components} />
    </Card>
  );
}

export function StatusPageEmbed({ appUrl }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [openGroups, setOpenGroups] = useState({});
  // Iter 122 — mirror the top status bar's daily maintenance window here so
  // the app section reads the same way (bright yellow / black text).
  const [statusInfo, setStatusInfo] = useState(null);
  const [maintNow, setMaintNow] = useState(false);
  const [minsLeft, setMinsLeft] = useState(null);

  useEffect(() => {
    api.get("/status").then((r) => setStatusInfo(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const tick = () => {
      setMaintNow(inMaintenanceWindow(statusInfo));
      setMinsLeft(minutesToWindowEnd(statusInfo));
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [statusInfo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/status/incident-io-page");
      setData(data);
      setErr(data?.error || "");
    } catch (e) {
      setErr(String(e?.message || e));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
    // Auto-refresh every 90s while the tab is open.
    const t = setInterval(load, 90 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400" data-testid="status-page-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!data?.configured) {
    return (
      <Card className="border-amber-200 bg-amber-50 p-8 text-center" data-testid="status-page-not-configured">
        <Info className="mx-auto h-8 w-8 text-amber-600" />
        <h3 className="mt-3 font-heading text-base font-semibold text-amber-900">Status Page not configured</h3>
        <p className="mx-auto mt-2 max-w-lg text-sm text-amber-800">
          A super-admin needs to enable the Widget API in the incident.io dashboard (Settings → Widget API)
          and paste the public URL into <span className="font-semibold">Settings → Dashboard → Status Page Widget API URL</span>.
        </p>
        <p className="mx-auto mt-2 max-w-lg text-[11px] text-amber-700/80">
          The hosted status page cannot be iframe-embedded because incident.io ships strict frame-ancestors
          headers — the Widget API is their documented alternative.
        </p>
        {appUrl && (
          <div className="mt-4">
            <Button variant="outline" onClick={() => window.open(appUrl, "_blank")} data-testid="status-page-open-external">
              <ExternalLink className="h-4 w-4" /> Open hosted status page
            </Button>
          </div>
        )}
      </Card>
    );
  }

  const ongoing = data.ongoing_incidents || [];
  const inProgress = data.in_progress_maintenances || [];
  const scheduled = data.scheduled_maintenances || [];
  const allClear = ongoing.length === 0 && inProgress.length === 0;
  const expandable = (data.groups || []).filter((g) => g.is_group);
  const groupCount = expandable.length;
  const allOpen = groupCount > 0 && expandable.every((g) => openGroups[g.id]);
  const toggleAll = () =>
    setOpenGroups(allOpen ? {} : Object.fromEntries(expandable.map((g) => [g.id, true])));
  const subscribeUrl = data.subscribe_url || appUrl || "";
  // A real outage always outranks the routine window.
  const maintWindow = maintNow && ongoing.length === 0;

  return (
    <div className="space-y-5" data-testid="status-page-embed">
      {/* Iter 120 — the intro notice authored on the incident.io status page
          (a markdown blockquote), shown verbatim above the banner. */}
      {data.intro_markdown && (
        <Card className="border-l-4 border-l-amber-400 bg-amber-50/60 p-4" data-testid="status-intro-notice">
          {data.intro_markdown.split("\n").map((raw) => raw.replace(/^>\s?/, "").trim()).filter(Boolean)
            .map((line, i) => (
              <p key={i} className={i === 0
                ? "font-heading text-sm font-bold uppercase tracking-wide text-amber-900"
                : "mt-1.5 text-sm text-amber-900/90"}>
                {line.replace(/\*\*/g, "")}
              </p>
            ))}
        </Card>
      )}

      {/* Overall banner */}
      <Card
        className={`border p-5 ${
          ongoing.length ? "border-red-200 bg-red-50"
          : maintWindow ? "border-yellow-500 bg-yellow-300"
          : inProgress.length ? "border-blue-200 bg-blue-50"
          : "border-emerald-200 bg-emerald-50"
        }`}
        data-testid="status-overall-banner"
        data-maintenance-window={maintWindow ? "1" : "0"}
      >
        <div className="flex items-start gap-3">
          {ongoing.length ? <AlertOctagon className="h-8 w-8 text-red-600" />
           : maintWindow ? <Wrench className="h-8 w-8 text-black" />
           : inProgress.length ? <Wrench className="h-8 w-8 text-blue-600" />
           : <CheckCircle2 className="h-8 w-8 text-emerald-600" />}
          <div className="min-w-0 flex-1">
            <h3 className={`font-heading text-lg font-semibold ${
              ongoing.length ? "text-red-900"
              : maintWindow ? "text-black"
              : inProgress.length ? "text-blue-900"
              : "text-emerald-900"
            }`} data-testid="status-overall-title">
              {ongoing.length
                ? `${ongoing.length} ongoing incident${ongoing.length === 1 ? "" : "s"}`
                : maintWindow
                  ? "Maintenance MAY BE underway"
                  : inProgress.length
                    ? `${inProgress.length} maintenance in progress`
                    : "All systems operational"}
              {maintWindow && minsLeft != null && (
                <span className="ml-2 rounded-full bg-black/80 px-2 py-0.5 align-middle text-[11px] font-semibold text-yellow-200"
                      data-testid="status-page-window-countdown">
                  {fmtLeft(minsLeft)}
                </span>
              )}
            </h3>
            <p className={`mt-1 text-sm ${
              ongoing.length ? "text-red-800"
              : maintWindow ? "text-black/80"
              : inProgress.length ? "text-blue-800"
              : "text-emerald-800"
            }`}>
              {maintWindow
                ? `Recurring daily maintenance window — we may or may not be performing any actions right now. Runs ${fmtHour(statusInfo?.maint_window_start_hour ?? 0)}–${fmtHour(statusInfo?.maint_window_end_hour ?? 2)} Mountain Time.`
                : allClear
                  ? "No active incidents. See scheduled maintenance below if any is planned."
                  : "Details below. Refreshes automatically every 90 seconds."}
            </p>
            {maintWindow && statusInfo?.maint_window_note && (
              <p className="mt-2 inline-flex rounded-full border border-black/30 bg-black/10 px-2.5 py-0.5 text-[11px] font-semibold text-black"
                 data-testid="status-page-maintenance-note">
                Tonight: {statusInfo.maint_window_note}
              </p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="status-refresh"
                  className="shrink-0 gap-1">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh
          </Button>
        </div>
        {/* Iter 121 — Subscribe to updates. incident.io's email/SMS signup is a
            client-side modal on their hosted page, so we deep-link partners
            straight to it; the RSS feed is a plain public URL. */}
        {!data.subscriptions_disabled && subscribeUrl && (
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/60 pt-3">
            <Button size="sm" onClick={() => window.open(subscribeUrl, "_blank", "noopener")}
                    data-testid="status-subscribe-btn" className="gap-1.5">
              <BellRing className="h-3.5 w-3.5" /> Subscribe to updates
            </Button>
            {data.rss_url && (
              <a href={data.rss_url} target="_blank" rel="noopener noreferrer"
                 data-testid="status-subscribe-rss"
                 className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-primary/40 hover:text-primary">
                <Rss className="h-3 w-3" /> RSS feed
              </a>
            )}
            <span className="text-[11px] text-slate-500">
              Get an email or SMS whenever an incident opens or closes.
            </span>
          </div>
        )}
        {err && (
          <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="status-error">
            Upstream feed returned an error: {err}. Showing the last cached snapshot.
          </p>
        )}
      </Card>

      {/* Iter 110g — 90-day per-component uptime grid, built from local
          snapshots of the Widget API. Grows over time. */}
      <StatusUptimeGrid />

      {/* Ongoing incidents */}
      {ongoing.length > 0 && (
        <section data-testid="status-ongoing-section">
          <div className="mb-2 flex items-center gap-2">
            <AlertOctagon className="h-4 w-4 text-red-600" />
            <h4 className="font-heading text-sm font-semibold text-slate-800">Ongoing incidents</h4>
          </div>
          <div className="space-y-3">
            {ongoing.map((i, idx) => <IncidentCard key={i.id || idx} item={i} testid={`status-ongoing-${i.id || idx}`} />)}
          </div>
        </section>
      )}

      {/* Iter 120 — System status: the component tree exactly as it appears on
          the public page (groups → components), with live impact status. */}
      {(data.groups || []).length > 0 && (
        <section data-testid="status-components-section">
          <div className="mb-2 flex items-center gap-2">
            <Boxes className="h-4 w-4 text-slate-500" />
            <h4 className="font-heading text-sm font-semibold text-slate-800">System status</h4>
            <span className="text-[11px] text-slate-400">last {data.history_window_days || 90} days</span>
            {groupCount > 0 && (
              <button type="button" onClick={toggleAll} data-testid="status-components-toggle-all"
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition-colors hover:border-primary/40 hover:text-primary">
                <ChevronRight className={`h-3 w-3 transition-transform ${allOpen ? "rotate-90" : ""}`} />
                {allOpen ? "Collapse all" : "Expand all"}
              </button>
            )}
          </div>
          <Card className="divide-y divide-slate-100 p-0">
            {(data.groups || []).map((g) => {
              const healthy = g.is_group
                ? g.components.every((c) => c.status === "operational")
                : (g.status || "operational") === "operational";
              const isOpen = !!openGroups[g.id];
              return (
                <div key={g.id} data-testid={`status-component-group-${g.id}`}
                     data-open={isOpen ? "1" : "0"}>
                  <button
                    type="button"
                    onClick={() => g.is_group && setOpenGroups((s) => ({ ...s, [g.id]: !s[g.id] }))}
                    disabled={!g.is_group}
                    data-testid={`status-component-group-toggle-${g.id}`}
                    className={`flex w-full items-center gap-2 px-4 py-3 text-left transition-colors ${
                      g.is_group ? "cursor-pointer hover:bg-slate-50" : "cursor-default"
                    }`}
                  >
                    {g.is_group ? (
                      <ChevronRight className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-90 text-primary" : ""}`} />
                    ) : (
                      <span className="w-4 shrink-0" />
                    )}
                    {healthy
                      ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                      : <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />}
                    <span className="truncate text-sm font-medium text-slate-800">{g.name}</span>
                    {g.is_group && (
                      <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500">
                        {g.components.length} component{g.components.length === 1 ? "" : "s"}
                      </span>
                    )}
                    <span className="ml-auto flex shrink-0 items-center gap-2">
                      <UptimePct value={g.uptime} value30={g.uptime_30d} since={g.uptime_since}
                                 days={data.history_window_days}
                                 testid={`status-uptime-group-${g.id}`} />
                      <span className="hidden text-xs font-medium text-slate-500 sm:inline">
                        {healthy ? "Operational" : "Impacted"}
                      </span>
                      {g.is_group && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          {isOpen ? "Hide" : "Show"}
                        </span>
                      )}
                    </span>
                  </button>
                  {g.is_group && isOpen && (
                    <ul className="space-y-1.5 border-t border-dashed border-slate-100 bg-slate-50/50 px-4 py-3 pl-12">
                      {g.components.map((c) => (
                        <li key={c.id} className="flex items-center gap-2 text-sm"
                          data-testid={`status-component-${c.id}`}>
                          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${c.status === "operational" ? "bg-emerald-500" : "bg-amber-500"}`} />
                          <span className="truncate text-slate-600">{c.name}</span>
                          <span className="ml-auto flex shrink-0 items-center gap-2">
                            <UptimePct value={c.uptime} value30={c.uptime_30d} since={c.uptime_since}
                                       days={data.history_window_days}
                                       testid={`status-uptime-component-${c.id}`} />
                            <span className="text-[11px] text-slate-400">
                              {c.status === "operational" ? "Operational" : c.status}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </Card>
        </section>
      )}

      {/* Iter 122 — Incident history under the component tree. */}
      {(data.history || []).length > 0 && (
        <IncidentHistory items={data.history} publicUrl={data.public_url} />
      )}

      {/* In-progress maintenances */}      {inProgress.length > 0 && (
        <section data-testid="status-inprogress-section">
          <div className="mb-2 flex items-center gap-2">
            <Wrench className="h-4 w-4 text-blue-600" />
            <h4 className="font-heading text-sm font-semibold text-slate-800">Maintenance in progress</h4>
          </div>
          <div className="space-y-3">
            {inProgress.map((i, idx) => <IncidentCard key={i.id || idx} item={i} testid={`status-inprogress-${i.id || idx}`} />)}
          </div>
        </section>
      )}

      {/* Scheduled */}
      {scheduled.length > 0 && (
        <section data-testid="status-scheduled-section">
          <div className="mb-2 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            <h4 className="font-heading text-sm font-semibold text-slate-800">Scheduled maintenance</h4>
          </div>
          <div className="space-y-3">
            {scheduled.map((i, idx) => <IncidentCard key={i.id || idx} item={i} testid={`status-scheduled-${i.id || idx}`} />)}
          </div>
        </section>
      )}

      {allClear && scheduled.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500" data-testid="status-empty">
          Nothing else to report.
        </div>
      )}

      {data.cached_at && (
        <p className="text-center text-[10px] text-slate-400" data-testid="status-cached-at">
          Data cached · fetched {fmtDate(new Date(data.cached_at * 1000).toISOString())}
        </p>
      )}
    </div>
  );
}
